import { inject, Service, signal } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, take } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { Md5 } from 'ts-md5';
import { Latency, WebSocketMessage, LatencyPing, isAction } from '@shared/models/websocket';
import { CognitoAuth } from '@core/auth/cognito-auth';
import { SsoAuth } from '@core/auth/sso-auth';
import { CurrentUser } from '@core/user/current-user';
import { WebSocketActionEnum } from '@shared/enums/websocket';
import { Logging } from '../logging';

@Service()
export class WebSocketClient {
  private readonly userService = inject(CurrentUser);
  private readonly cognitoAuthService = inject(CognitoAuth);
  private readonly ssoAuthService = inject(SsoAuth);
  private readonly logger = inject(Logging);

  // Replaying subjects: late subscribers (live, waiting room, live creation) rely on the last message.
  private readonly messageSource = new BehaviorSubject<WebSocketMessage | null>(null);
  private readonly userMessageSource = new BehaviorSubject<WebSocketMessage | null>(null);
  private readonly drawEventMessageSource = new BehaviorSubject<WebSocketMessage | null>(null);
  private readonly chatMessageSource = new BehaviorSubject<WebSocketMessage | null>(null);
  private readonly latencyState = signal<Latency>({ latency: 0, canSendMessage: true });
  private readonly medinboxMessageSource = new BehaviorSubject<WebSocketMessage | null>(null);
  private readonly generalSocketReconnectedSource = new BehaviorSubject<boolean>(false);

  public readonly message$: Observable<WebSocketMessage | null> = this.messageSource.asObservable();
  public readonly userMessage$: Observable<WebSocketMessage | null> = this.userMessageSource.asObservable();
  public readonly drawEventMessage$: Observable<WebSocketMessage | null> = this.drawEventMessageSource.asObservable();
  public readonly chatMessage$: Observable<WebSocketMessage | null> = this.chatMessageSource.asObservable();
  public readonly latency = this.latencyState.asReadonly();
  public readonly medinboxMessage$: Observable<WebSocketMessage | null> = this.medinboxMessageSource.asObservable();
  public readonly generalSocketReconnected$: Observable<boolean> = this.generalSocketReconnectedSource.asObservable();

  private socketMedinbox: Socket | null = null;
  private generalSocket: Socket | null = null;
  private latencySocket: Socket | null = null;
  private currentLatencyLiveCode: string | null = null;
  private currentGeneralSocketLiveCode: string | null = null;
  private currentMedinboxEquipmentId: string | null = null;

  /** Shared by all sockets, so concurrent reconnects trigger a single token refresh. */
  private refreshJwtPromise: Promise<string> | null = null;

  private refreshJwt(): Promise<string> {
    if (!this.refreshJwtPromise) {
      // Must stay in line with the token chosen by auth-interceptor.
      const token$ = this.userService.usesCognito() ? this.cognitoAuthService.getIdToken() : this.ssoAuthService.getAccessToken();
      this.refreshJwtPromise = lastValueFrom(token$.pipe(take(1))).finally(() => {
        this.refreshJwtPromise = null;
      });
    }
    return this.refreshJwtPromise;
  }

  private isAuthenticated(): Promise<boolean> {
    return lastValueFrom(this.userService.isAuthenticated().pipe(take(1)));
  }

  /** Called by socket.io on every connection attempt, reconnects included, so each gets a fresh token. */
  private readonly authCallback = (cb: (data: object) => void) => {
    void this.refreshJwt().then((token) => cb({ token }));
  };

  public get hasMedinboxChannel(): boolean {
    return this.socketMedinbox != null;
  }

  public clearChat(): void {
    this.chatMessageSource.next(null);
  }

  public async initMedinboxChannel(medinboxEquipmentId: string, allowNotMedinboxUser = false): Promise<boolean> {
    if (!this.socketMedinbox) {
      if ((await this.isAuthenticated()) && (allowNotMedinboxUser || this.userService.isEquipment())) {
        // A concurrent caller may have opened it during the await: a second socket would deliver every box message twice.
        if (this.socketMedinbox) return true;
        this.currentMedinboxEquipmentId = medinboxEquipmentId;
        const channel = `MedinboxEquipment${this.userService.isEquipment() ? 'Private' : 'Control'}-${medinboxEquipmentId}`;
        this.socketMedinbox = this.openMedinboxSocket(channel);
        this.subscribeToMedinboxChannels(channel);

        this.socketMedinbox.io.on('reconnect', () => {
          this.logger.debug('[Medinbox Socket] Successfully reconnected');
          const channel = `MedinboxEquipment${this.userService.isEquipment() ? 'Private' : 'Control'}-${this.currentMedinboxEquipmentId}`;
          this.subscribeToMedinboxChannels(channel);
        });

        return true;
      } else {
        console.warn('Could not connect to MedinboxSocket');
        return false;
      }
    } else {
      return true;
    }
  }

  private openMedinboxSocket(channel: string): Socket {
    return io(`${environment.webSocketUrl}`, { query: { channels: channel }, auth: this.authCallback });
  }

  private subscribeToMedinboxChannels(channel: string) {
    if (channel) {
      this.socketMedinbox!.off(channel);
    }

    this.socketMedinbox!.on(channel, (message: unknown) => {
      if (Array.isArray(message)) {
        this.medinboxMessageSource.next(message[0] as WebSocketMessage);
      } else {
        this.medinboxMessageSource.next(message as WebSocketMessage);
      }
    });
  }

  public async initWebSocketService(liveCode: string): Promise<void> {
    if (await this.isAuthenticated()) {
      if (!this.latencySocket) {
        this.initLatencySocket(liveCode);
      }
      if (this.generalSocket && this.currentGeneralSocketLiveCode !== liveCode) {
        this.generalSocket.disconnect();
        this.generalSocket = null;
        this.currentGeneralSocketLiveCode = null;
        this.messageSource.next(null);
        this.userMessageSource.next(null);
        this.drawEventMessageSource.next(null);
      }
      if (!this.generalSocket) {
        await new Promise<void>((resolve) => {
          this.generalSocket = io(`${environment.webSocketUrl}`, {
            query: {
              channels: `BackendControlLive-${liveCode},UserControlLive-${liveCode},DrawLive-${liveCode},ChatLive-${liveCode},UserToUser-${Md5.hashStr(
                this.userService.currentUserId(),
              )}`,
              live: liveCode,
            },
            auth: this.authCallback,
          });
          this.subscribeToGeneralsChannels();

          this.generalSocket.once('connect', () => resolve());
          this.generalSocket.once('connect_error', () => resolve());

          this.generalSocket.io.on('reconnect', () => {
            this.logger.debug('[General Socket] Successfully reconnected');
            this.subscribeToGeneralsChannels();
            this.generalSocketReconnectedSource.next(true);
          });
        });
        this.currentGeneralSocketLiveCode = liveCode;
      }
    }
  }

  private subscribeToGeneralsChannels() {
    this.generalSocket!.offAny();

    this.generalSocket!.onAny((channel: string, message: unknown) => {
      if (message) {
        const wsMessage = (Array.isArray(message) ? message[0] : message) as WebSocketMessage;
        if (channel.indexOf('DrawLive') !== -1) {
          this.drawEventMessageSource.next(wsMessage);
        } else if (channel.indexOf('UserToUser') !== -1) {
          this.userMessageSource.next(wsMessage);
        } else if (channel.indexOf(`ChatLive`) !== -1) {
          this.chatMessageSource.next(wsMessage);
        } else {
          this.messageSource.next(wsMessage);
        }
      }
    });
  }

  private initLatencySocket(currentLiveCode: string) {
    this.currentLatencyLiveCode = currentLiveCode;

    this.latencySocket = io(`${environment.webSocketUrl}`, {
      query: {
        channels: `Latency`,
      },
      auth: this.authCallback,
    });
    this.subscribeToLatencyChannel(currentLiveCode);

    this.latencySocket.io.on('reconnect', () => {
      this.logger.debug('[Latency Socket] Successfully reconnected');
      if (this.currentLatencyLiveCode) {
        this.subscribeToLatencyChannel(this.currentLatencyLiveCode);
      }
    });
  }

  private subscribeToLatencyChannel(currentLiveCode: string) {
    this.latencySocket!.off('Latency');

    this.latencySocket!.on(`Latency`, (message: unknown) => {
      if (message) {
        const pingMessage = message as WebSocketMessage;
        if (isAction(pingMessage, WebSocketActionEnum.PING)) {
          // The latency channel only carries the server's ping.
          this.latencyState.set({
            latency: (pingMessage.message.params as LatencyPing).latency,
            canSendMessage: (pingMessage.message.params as LatencyPing).canSendMessage,
          });
          this.latencySocket!.emit(`Latency`, {
            audience: 'WEBSOCKETSERVER',
            message: {
              action: WebSocketActionEnum.PONG,
            },
            sourceId: this.userService.currentUserId(),
            liveCode: currentLiveCode,
            destinationId: 'WEBSOCKETSERVER',
          } as WebSocketMessage);
        }
      }
    });
  }

  public async sendMessage(message: WebSocketMessage) {
    if (this.generalSocket) {
      this.generalSocket.emit(`UserControlLive-${message.destinationId}`, message);
    }
  }

  public async sendDrawEventMessage(message: WebSocketMessage): Promise<void> {
    if (this.generalSocket) {
      this.generalSocket.emit(`DrawLive-${message.destinationId}`, message);
    }
  }

  public async SendChatMessage(message: WebSocketMessage) {
    if (this.generalSocket) {
      this.generalSocket.emit(`ChatLive-${message.destinationId}`, message);
    }
  }

  public async sendUserMessage(message: WebSocketMessage) {
    if (this.generalSocket) {
      this.generalSocket.emit(`UserToUser-${Md5.hashStr(message.destinationId)}`, message);
    }
  }

  public async sendMedinboxMessage(message: WebSocketMessage) {
    let initiated = this.socketMedinbox != null;
    if (!initiated) {
      // Any participant may open the channel: the websocket server enforces per-action permissions.
      initiated = await this.initMedinboxChannel(message.destinationId, true);
    }
    if (initiated) {
      this.socketMedinbox!.emit(`MedinboxEquipment${this.userService.isEquipment() ? 'Private' : 'Control'}-${message.destinationId}`, message);
    } else {
      console.warn("can't send medinbox message socket not initiated");
    }
  }

  public async closeWebSockets() {
    if (this.socketMedinbox && !this.userService.isEquipment()) {
      this.socketMedinbox.disconnect();
      this.socketMedinbox = null;
      this.medinboxMessageSource.next(null);
    }
    if (this.latencySocket) {
      this.latencySocket.disconnect();
      this.latencySocket = null;
      this.latencyState.set({ latency: 0, canSendMessage: true });
    }
    if (this.generalSocket) {
      this.generalSocket.disconnect();
      this.userMessageSource.next(null);
      this.messageSource.next(null);
      this.drawEventMessageSource.next(null);
      this.generalSocket = null;
      this.currentGeneralSocketLiveCode = null;
    }
  }
}
